import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { DialogTitleWithCloseButton, DraggablePaperGenerator } from '@carsys/ui'
import { TicketForm } from './TicketForm'
import { GeneralFormFields } from '@cbgms/components/src/support-request/fields/GeneralFormFields'
import { VehicleFormFields } from '@cbgms/components/src/support-request/fields/VehicleFormFields'
import { PartFormFields } from '@cbgms/components/src/support-request/fields/PartFormFields'
import { TicketTypeFormFields } from '@cbgms/components/src/support-request/fields/TicketTypeField'
import { QuestionFormFields } from '@cbgms/components/src/support-request/fields/QuestionFields'
import { ContactPreferenceFields } from '@cbgms/components/src/support-request/fields/ContactPreferenceFields'
import { hasContactPreferenceFields, hasPartFields, hasVehicleFields } from '@cbgms/components/src/support-request/utils'
import { SubmitButton } from '@carsys/form'

const dragID = 'drag-ticket-form'

interface Props extends React.ComponentProps<typeof TicketForm> {
  open: boolean
  handleClose: () => void
}

export const TicketDialog: React.FC<Omit<Props, 'handleOnSubmitSuccess' | 'children'>> = ({
  open,
  handleClose,
  shopID,
  shopName,
  employee,
  orderAccount,
  vehicle,
  product,
  preselectedTicketType
}) => {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth scroll='paper' PaperComponent={DraggablePaperGenerator(dragID)}>
      <DialogTitleWithCloseButton onClose={handleClose} draggableIDHandler={dragID}>
        {t('Contact.RaiseTicket')}
      </DialogTitleWithCloseButton>
      <TicketForm
        handleOnSubmitSuccess={handleClose}
        shopID={shopID}
        shopName={shopName}
        employee={employee}
        product={product}
        vehicle={vehicle}
        orderAccount={orderAccount}
        preselectedTicketType={preselectedTicketType}
      >
        {({ values }) => (
          <>
            <DialogContent>
              <Grid container spacing={2} direction='column'>
                <Grid item>
                  <TicketTypeFormFields />
                </Grid>
                <Grid item>
                  <GeneralFormFields />
                </Grid>
                {hasContactPreferenceFields(values.TicketType) && (
                  <Grid item>
                    <ContactPreferenceFields />
                  </Grid>
                )}
                {hasVehicleFields(values.TicketType) && (
                  <Grid item>
                    <VehicleFormFields preFilled={!!vehicle} />
                  </Grid>
                )}
                {hasPartFields(values.TicketType) && (
                  <Grid item>
                    <PartFormFields preFilled={!!product} />
                  </Grid>
                )}
                <Grid item>
                  <QuestionFormFields />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button color='primary' variant='outlined' onClick={() => handleClose()}>
                {t('global.close')}
              </Button>
              <SubmitButton startIcon={<SendIcon />}>{t('global.submit')}</SubmitButton>
            </DialogActions>
          </>
        )}
      </TicketForm>
    </Dialog>
  )
}
